/**
 * Centralized Google Maps API Loader
 * Prevents multiple script loads and handles initialization properly
 */

let isGoogleMapsLoaded = false;
let isGoogleMapsLoading = false;
let loadPromise = null;

const GOOGLE_MAPS_API_KEY = 'AIzaSyDcSIgP5JIYkfFFcXiJhLRK879P6mblIjY';

/**
 * Load Google Maps API with Places library
 * @returns {Promise} Promise that resolves when Google Maps is loaded
 */
export const loadGoogleMaps = () => {
  return new Promise((resolve, reject) => {
    // If already loaded, resolve immediately
    if (isGoogleMapsLoaded && window.google && window.google.maps && window.google.maps.places) {
      resolve(window.google);
      return;
    }

    // If currently loading, return the existing promise
    if (isGoogleMapsLoading && loadPromise) {
      loadPromise.then(resolve).catch(reject);
      return;
    }

    // Start loading
    isGoogleMapsLoading = true;
    loadPromise = new Promise((innerResolve, innerReject) => {
      // Check if script already exists
      const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
      if (existingScript) {
        // Script exists, wait for it to load
        existingScript.addEventListener('load', () => {
          if (window.google && window.google.maps && window.google.maps.places) {
            isGoogleMapsLoaded = true;
            isGoogleMapsLoading = false;
            innerResolve(window.google);
          } else {
            innerReject(new Error('Google Maps failed to load properly'));
          }
        });
        existingScript.addEventListener('error', () => {
          isGoogleMapsLoading = false;
          innerReject(new Error('Failed to load Google Maps script'));
        });
        return;
      }

      // Create new script
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;
      
      // Set up global callback
      window.initGoogleMaps = () => {
        if (window.google && window.google.maps && window.google.maps.places) {
          isGoogleMapsLoaded = true;
          isGoogleMapsLoading = false;
          delete window.initGoogleMaps; // Clean up global callback
          innerResolve(window.google);
        } else {
          isGoogleMapsLoading = false;
          delete window.initGoogleMaps;
          innerReject(new Error('Google Maps failed to initialize properly'));
        }
      };

      script.onerror = () => {
        isGoogleMapsLoading = false;
        delete window.initGoogleMaps;
        innerReject(new Error('Failed to load Google Maps script'));
      };

      document.head.appendChild(script);
    });

    loadPromise.then(resolve).catch(reject);
  });
};

/**
 * Check if Google Maps is ready
 * @returns {boolean}
 */
export const isGoogleMapsReady = () => {
  return isGoogleMapsLoaded && window.google && window.google.maps && window.google.maps.places;
};

/**
 * Get Google Maps instance (throws if not loaded)
 * @returns {Object} Google Maps object
 */
export const getGoogleMaps = () => {
  if (!isGoogleMapsReady()) {
    throw new Error('Google Maps is not loaded yet. Call loadGoogleMaps() first.');
  }
  return window.google;
};
